import { type DateValue, getLocalTimeZone, parseDate, today } from "@internationalized/date";
import { useMemo } from "react";
import { create } from "zustand";
import { useDateLimits } from "@/api/date-limits";

const end = today(getLocalTimeZone());
// Last 31 days: 31 days ago to today
const start = end.subtract({ days: 31 });

export const calculateDashboardDateRange = (
  storedRange: { start: DateValue; end: DateValue } | null | undefined,
  limits: { minDate: DateValue; maxDate: DateValue } | undefined,
): { start: DateValue; end: DateValue } | null => {
  if (storedRange === undefined && limits === undefined) return null;

  if (!storedRange) {
    // Determine the end date: earlier of today or limits maxDate
    const effectiveEnd = limits && limits.maxDate < end ? limits.maxDate : end;

    // Determine the start date: 31 days before end, or limits minDate (whichever is later)
    const thirtyOneDaysBeforeEnd = effectiveEnd.subtract({ days: 31 });
    const effectiveStart = limits && limits.minDate > thirtyOneDaysBeforeEnd ? limits.minDate : thirtyOneDaysBeforeEnd;

    return { start: effectiveStart, end: effectiveEnd };
  }

  return {
    start: storedRange.start,
    end: storedRange.end,
  };
};

const useDashboardDateStore = create<{
  dateRange: { start: DateValue; end: DateValue } | null;
  setDateRange: (range: { start: DateValue; end: DateValue } | null) => void;
}>((set) => ({
  dateRange: null,
  setDateRange: (range) => set(() => ({ dateRange: range })),
}));

export const useDashboardDateFilter = () => {
  const { data: dateLimits } = useDateLimits();
  const storedRange = useDashboardDateStore((state) => state.dateRange);
  const setStoredRange = useDashboardDateStore((state) => state.setDateRange);

  const limits = useMemo(() => {
    if (!dateLimits) return undefined;
    return {
      minDate: parseDate(dateLimits.minDate),
      maxDate: parseDate(dateLimits.maxDate),
    };
  }, [dateLimits]);

  const dateRange: { start: DateValue; end: DateValue } | null = useMemo(() => {
    return calculateDashboardDateRange(storedRange, limits);
  }, [storedRange, limits]);

  const changeDate = (range: { start: DateValue; end: DateValue } | null) => {
    if (range) setStoredRange(range);
    else setStoredRange(null);
  };

  const clear = () => {
    setStoredRange(null);
  };

  return { dateRange, isApplied: !!storedRange, changeDate, clear, limits };
};
